/**
 * Vo ung dung: thanh tren cung, dieu huong, va khung noi dung.
 *
 * Hai quyet dinh bo cuc dang noi:
 *   - So du LUON hien o thanh tren cung. Nguoi choi kiem tra no lien tuc; bat ho
 *     mo trang khac de xem so du la loi thiet ke co ban cua dong san pham nay.
 *   - Man hinh hep dung thanh dieu huong DUOI cung. Ngon tay cai voi toi de hon,
 *     va thanh tren cung con phai nhuong cho so du.
 */

import type { JSX } from 'react';
import { NavLink, Link, Outlet } from 'react-router-dom';
import { useSession, usePrimaryWallet } from '../lib/session';
import { formatMoney } from '../lib/money';
import { useTheme } from '../themes/ThemeProvider';
import { BrandMark } from './BrandMark';
import { Button } from './ui';
import styles from './Shell.module.css';

const PRIMARY_NAV = [
  { to: '/the-thao', label: 'Thể thao' },
  { to: '/song-bai', label: 'Sòng bài' },
  { to: '/quay-hu', label: 'Quay hũ' },
  { to: '/xo-so', label: 'Xổ số' },
  { to: '/lo-de', label: 'Lô đề' },
  { to: '/khuyen-mai', label: 'Khuyến mại' },
];

const MOBILE_NAV = [
  { to: '/the-thao', label: 'Thể thao', icon: '⚽' },
  { to: '/song-bai', label: 'Sòng bài', icon: '♠' },
  { to: '/quay-hu', label: 'Quay hũ', icon: '🎰' },
  { to: '/vi', label: 'Ví', icon: '💰' },
  { to: '/cuoc-cua-toi', label: 'Cược', icon: '🧾' },
];

export function Shell(): JSX.Element {
  const { isAuthenticated, me, logout } = useSession();
  const wallet = usePrimaryWallet();
  const { theme } = useTheme();

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.brand} aria-label="Trang chủ">
            <BrandMark mark={theme.mark} className={styles.brandMark} />
            <span className={styles.brandName}>{theme.brandName}</span>
          </Link>

          <nav className={styles.primaryNav} aria-label="Điều hướng chính">
            {PRIMARY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [styles.navLink, isActive ? styles.navLinkActive : ''].filter(Boolean).join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={styles.headerRight}>
            {isAuthenticated ? (
              <>
                {wallet && (
                  <Link to="/vi" className={styles.balance} title="Số dư khả dụng">
                    <span className={styles.balanceLabel}>Số dư</span>
                    <span className={`numeric ${styles.balanceValue}`}>
                      {formatMoney(wallet.available)}
                      <span className={styles.balanceCurrency}>{wallet.currency}</span>
                    </span>
                    {/* Vi thuong hien RIENG: gop chung se lam nguoi choi tuong
                        rut duoc ca phan thuong chua hoan thanh vong cuoc. */}
                    {BigInt(wallet.bonus.amount_minor) > 0n && (
                      <span className={styles.balanceBonus}>
                        + {formatMoney(wallet.bonus)} thưởng
                      </span>
                    )}
                  </Link>
                )}
                <Link to="/cuoc-cua-toi" className={`${styles.navLink} ${styles.hideNarrow}`}>
                  Cược của tôi
                </Link>
                <Link to="/tai-khoan" className={styles.navLink}>
                  Tài khoản
                </Link>
                {/* Man hinh hep: nut Thoat nam o trang Tai khoan, nhuong cho cho so du va Nap tien. */}
                <span className={styles.hidePhone}>
                  <Button size="sm" onClick={() => void logout()} variant="ghost">
                    Thoát
                  </Button>
                </span>
                <Link to="/nap-tien" className={styles.depositLink}>
                  Nạp tiền
                </Link>
              </>
            ) : (
              <>
                <Link to="/dang-nhap">
                  <Button size="sm" variant="secondary">Đăng nhập</Button>
                </Link>
                <Link to="/dang-ky">
                  <Button size="sm">Đăng ký</Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Nhac thuc te phien choi: bat buoc khi goi phap ly yeu cau (RSK-R20) */}
        {me?.reality_check_due && (
          <div className={styles.realityCheck} role="status">
            Phiên chơi đã kéo dài một khoảng thời gian.{' '}
            <Link to="/tai-khoan/hoat-dong" className={styles.realityCheckLink}>
              Xem tổng kết phiên
            </Link>
          </div>
        )}
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <nav className={styles.mobileNav} aria-label="Điều hướng di động">
        {MOBILE_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [styles.mobileNavItem, isActive ? styles.mobileNavItemActive : '']
                .filter(Boolean)
                .join(' ')
            }
          >
            <span className={styles.mobileNavIcon} aria-hidden="true">{item.icon}</span>
            <span className={styles.mobileNavLabel}>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
